import { type FC, useCallback, useEffect, useState } from "react";

import type { SourceControlProvider } from "../../types";

import { setupGitHubAppInstallation } from "../../api/sourceControl";
import {
  createSourceControlProviderAsUser,
  listSourceControlProvidersAsUser,
} from "../../dashboard-client";
import { useDashboardAuth } from "../../hooks/useDashboardAuth";
import { useSourceControlProviderDetail } from "../../hooks/useSourceControlProviderDetail";
import { Alert } from "../common/Alert";
import { Button } from "../common/Button";
import { EmptyState } from "../common/EmptyState";
import { LoadingSpinner } from "../common/LoadingState";
import { GitHubAppInstallCard } from "./GitHubAppInstallCard";
import { PushBrokerCard } from "./PushBrokerCard";
import SourceControlRepositoryList from "./SourceControlRepositoryList";

type ViewState =
  | { status: "loading" }
  | { status: "not-connected" }
  | { status: "connected"; providerId: string }
  | { status: "error"; message: string };

const SourceControlPanel: FC = () => {
  const { organizationId } = useDashboardAuth();
  const [view, setView] = useState<ViewState>({ status: "loading" });
  const [connecting, setConnecting] = useState(false);

  const providerId = view.status === "connected" ? view.providerId : null;

  const {
    detail,
    loading: detailLoading,
    error: detailError,
    upsertRepository,
    deleteRepository,
  } = useSourceControlProviderDetail(providerId);

  const loadProviders = useCallback(async () => {
    setView({ status: "loading" });
    try {
      const listResult = await listSourceControlProvidersAsUser();
      const { providers } = listResult.data;
      const github = providers.find(
        (p: SourceControlProvider) => p.type === "github",
      );
      if (github) {
        setView({ providerId: github.provider_id, status: "connected" });
      } else {
        setView({ status: "not-connected" });
      }
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to load source control providers.";
      setView({ message, status: "error" });
    }
  }, []);

  useEffect(() => {
    void loadProviders();
  }, [loadProviders]);

  const handleConnect = async () => {
    if (!organizationId) {
      return;
    }
    setConnecting(true);
    try {
      const listResult2 = await listSourceControlProvidersAsUser();
      const { providers } = listResult2.data;
      const existing = providers.find(
        (p: SourceControlProvider) => p.type === "github",
      );

      let githubProvider = existing;
      if (!githubProvider) {
        const createResult = await createSourceControlProviderAsUser({
          body: {
            display_name: "GitHub",
            provider_id: `github-${organizationId}`,
            tenant_id: organizationId,
            type: "github",
          },
        });
        githubProvider = createResult.data.provider;
      }

      const redirectUrl = `${window.location.pathname}${window.location.search}`;
      const { redirect_url } = await setupGitHubAppInstallation(
        githubProvider.provider_id,
        { redirect_url: redirectUrl },
      );
      window.location.assign(redirect_url);
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to start GitHub install.";
      setView({ message, status: "error" });
      setConnecting(false);
    }
  };

  if (view.status === "loading") {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner />
      </div>
    );
  }

  if (view.status === "error") {
    return (
      <div className="space-y-4">
        <Alert variant="warning">{view.message}</Alert>
        <Button
          type="button"
          variant="secondary"
          onClick={() => void loadProviders()}
        >
          Retry
        </Button>
      </div>
    );
  }

  if (view.status === "not-connected") {
    return (
      <EmptyState
        title="GitHub not connected"
        description="Connect your GitHub organization to enable source control integration."
        className="py-12"
        action={
          <Button
            type="button"
            variant="primary"
            onClick={() => void handleConnect()}
            loading={connecting}
            loadingText="Connecting…"
            disabled={!organizationId}
          >
            Connect GitHub
          </Button>
        }
      />
    );
  }

  return (
    <div className="space-y-4">
      {detailError && <Alert variant="warning">{detailError}</Alert>}

      {detailLoading && !detail && (
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner />
        </div>
      )}

      {detail && (
        <>
          <GitHubAppInstallCard providerId={view.providerId} detail={detail} />

          <PushBrokerCard
            providerId={view.providerId}
            repositorySlugsById={
              new Map(
                detail.repositories.map((repository) => [
                  repository.repository_id,
                  repository.remote_slug ?? repository.repository_id,
                ]),
              )
            }
          />

          <SourceControlRepositoryList
            providerId={view.providerId}
            repositories={detail.repositories}
            onUpsert={async (_pid, input) => {
              await upsertRepository(input);
            }}
            onDelete={async (_pid, repositoryId) => {
              await deleteRepository(repositoryId);
            }}
          />
        </>
      )}
    </div>
  );
};

export default SourceControlPanel;
